import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { UserProfile, FinancialConfig, Quote, AppView, ProfileStep } from '../types';
import { saveState } from '../store';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { isMasterEmail } from '../constants';
import { safeStorage } from '../utils/safeStorage';
import Onboarding from './Onboarding';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import { useMercadoPagoPolling } from '../hooks/useMercadoPagoPolling';
import { useAppDataSync } from '../hooks/useAppDataSync';
import { useAppNavigation } from '../hooks/useAppNavigation';
import { SpecialistTab } from '../modules/erp/components/SpecialistBottomNav';
import { ActiveView } from '../modules/erp/hooks/useErpPlaygroundState';
import { PaywallInitialPlan } from './PaywallModal';
import { CostCenterTab } from '../hooks/useCostCenterTabs';
import { 
  Receipt, 
  ClinicalPatient, 
  ClinicalConsultation, 
  ConsumedMaterialItem, 
  StockMovement, 
  ReceptionOrderItem 
} from '../modules/erp/types';
import { EnterpriseSuiteLayout } from '../modules/erp/enterprise';
import { EnterpriseVerticalId, detectVerticalFromNiche } from '../modules/erp/enterprise/config/enterpriseVerticals';
import { generateCfmvSignatureHash, generateReceiptNumber } from '../modules/erp/utils/cryptoUtils';

import { CoreViewDispatcher } from './shell/CoreViewDispatcher';
import { SpecialistViewDispatcher } from './shell/SpecialistViewDispatcher';
import { EnterpriseViewDispatcher } from './shell/EnterpriseViewDispatcher';
import { GlobalModalsContainer } from './shell/GlobalModalsContainer';
import { useVirtualKeyboard } from '../hooks/useVirtualKeyboard';
import WelcomeGuidedTourModal from './onboarding/WelcomeGuidedTourModal';
import { claimPendingSimulationForUser, consumePendingCheckoutPlan } from '../lib/batchSimulatorStorageBridge';

export default function AppShell() {
  const { session, signOut } = useAuth();
  const setViewRef = useRef<React.Dispatch<React.SetStateAction<AppView>> | null>(null);
  const [tempNiche, setTempNiche] = useState('');
  const [tempService, setTempService] = useState('');

  // Resgate automático de simulação em lote vinda de visitante e checkout pendente
  useEffect(() => {
    if (session?.user?.id) {
      const claimed = claimPendingSimulationForUser(session.user.id);
      
      try {
        const params = new URLSearchParams(window.location.search);
        const urlAction = params.get('action');
        const urlPlan = params.get('plan');
        const urlView = params.get('view');
        
        const pendingPlan = consumePendingCheckoutPlan() || (urlAction === 'checkout' ? urlPlan : null);

        if (pendingPlan && session.access_token) {
          if (['reforma_mensal', 'reforma_anual', 'passe_projeto_90d'].includes(pendingPlan)) {
            (async () => {
              try {
                const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-checkout-preference`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${session.access_token}`,
                  },
                  body: JSON.stringify({ plano: pendingPlan, returnView: 'reforma_lote' }),
                });
                if (res.ok) {
                  const data = await res.json();
                  if (data?.init_point) {
                    window.location.href = data.init_point;
                    return;
                  }
                }
              } catch (err) {
                console.warn('Erro ao disparar checkout de reforma tributária:', err);
              }
            })();
          }
        }

        if (claimed && !urlView) {
          setViewRef.current?.('reforma_lote');
        }
      } catch (e) {
        console.warn('Erro ao processar retenção de simulação de reforma:', e);
      }
    }
  }, [session?.user?.id, session?.access_token]);

  // Estados dos Modais
  const [teamModalOpen, setTeamModalOpen] = useState(false);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [upgradeRequiredTier, setUpgradeRequiredTier] = useState<'pro' | 'premium' | 'enterprise'>('pro');
  const [upgradeFeatureName, setUpgradeFeatureName] = useState('');
  const [upgradeFeatureDesc, setUpgradeFeatureDesc] = useState<string | undefined>(undefined);
  const [activeConsultationPatient, setActiveConsultationPatient] = useState<ClinicalPatient | null>(null);
  const [isAccountantPortalMode, setIsAccountantPortalMode] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      try {
        const params = new URLSearchParams(window.location.search);
        return params.get('mode') === 'accountant' || params.get('portal_contador') === 'true';
      } catch {
        return false;
      }
    }
    return false;
  });
  const [paywallModalOpen, setPaywallModalOpen] = useState(false);
  const [paywallInitialPlan, setPaywallInitialPlan] = useState<PaywallInitialPlan>('annual');
  const [isActivatingTrial, setIsActivatingTrial] = useState(false);
  const [isWelcomeTourOpen, setIsWelcomeTourOpen] = useState(false);
  const { isKeyboardOpen } = useVirtualKeyboard();

  // Parâmetro de paywall na URL
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const paywallParam = (params.get('paywall') || '').toLowerCase();
      if (paywallParam) {
        if (paywallParam.includes('enterprise') || paywallParam.includes('empresarial')) {
          setPaywallInitialPlan('enterprise_anual_pix');
          setPaywallModalOpen(true);
        } else if (paywallParam === 'specialist' || paywallParam === 'especialista' || paywallParam === 'premium') {
          setPaywallInitialPlan('specialist');
          setPaywallModalOpen(true);
        } else if (paywallParam === 'specialist_annual' || paywallParam === 'especialista_anual' || paywallParam === 'premium_anual') {
          setPaywallInitialPlan('specialist_annual');
          setPaywallModalOpen(true);
        } else if (paywallParam === 'monthly' || paywallParam === 'pro' || paywallParam === 'pro_mensal') {
          setPaywallInitialPlan('monthly');
          setPaywallModalOpen(true);
        } else if (paywallParam === 'annual' || paywallParam === 'pro_anual' || paywallParam === '1' || paywallParam === 'true') {
          setPaywallInitialPlan('annual');
          setPaywallModalOpen(true);
        }
      }
    } catch (e) {
      console.warn('Erro ao processar parâmetro de paywall na URL:', e);
    }
  }, []);

  const setViewStable = useCallback((v: AppView) => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const urlView = urlParams.get('view');
      // Respeita deep linking legítimo (ex: ?view=work-orders, ?view=calendar), mas não trava em 'chat' legado
      if (urlView && urlView !== 'chat') {
        setViewRef.current?.(urlView as AppView);
        return;
      }
    } catch {}
    setViewRef.current?.(v);
  }, []);

  const {
    isLoadingUserData,
    hasLoadedAllData,
    user,
    config,
    quotes,
    materials,
    recipes,
    services,
    clients,
    suppliers,
    workOrders,
    receipts,
    appointments,
    staffMembers,
    subscriptionClubs,
    storefrontProfile,
    isFirstVisit,
    setIsFirstVisit,
    loadUserData,
    handleOnboardingComplete,
    handleResetNicheData,
    handleProfileUpdate,
    handleSaveQuote,
    handleSaveRecipe,
    handleDeleteRecipe,
    handleSaveService,
    handleDeleteService,
    handleSaveMaterials,
    handleUpdateQuoteStatus,
    handleSaveClient,
    handleDeleteClient,
    handleSaveSupplier,
    handleDeleteSupplier,
    handleSaveWorkOrder,
    handleDeleteWorkOrder,
    handleSaveReceipt,
    handleDeleteReceipt,
    handleSaveAppointment,
    handleDeleteAppointment,
    handleUpdateAppointmentStatus,
    refreshAppointments,
    handleSaveSignature,
    handleSaveStaffMember,
    handleDeleteStaffMember,
    handleSaveStorefrontProfile,
    handleSaveSubscriptionClub,
    handleDeleteSubscriptionClub,
    inventoryBatches,
    stockMovements,
    clinicalPatients,
    clinicalConsultations,
    clinicalVaccinations,
    clinicalExams,
    accountantAccesses,
    handleSaveInventoryBatch,
    handleDeleteInventoryBatch,
    handleSaveStockMovement,
    handleSaveClinicalPatient,
    handleDeleteClinicalPatient,
    handleSaveClinicalConsultation,
    handleSaveClinicalVaccination,
    handleDeleteClinicalVaccination,
    handleSaveClinicalExam,
    handleDeleteClinicalExam,
    handleInviteAccountant,
    handleRevokeAccountantAccess,
    syncConfigToSupabase,
    setUser,
    setConfig,
    setQuotes,
    setMaterials,
    setRecipes,
    setServices,
    setClients,
    setSuppliers,
    setWorkOrders,
    setReceipts,
    setAppointments,
    setStaffMembers,
    setSubscriptionClubs,
    setStorefrontProfile,
    lastLoadedId
  } = useAppDataSync({ 
    session, 
    setView: setViewStable
  });

  const {
    view,
    setView,
    profileStep,
    initialQuoteDraft,
    setInitialQuoteDraft,
    navigateTo,
    handleGoBack,
    handleReuseQuote
  } = useAppNavigation({ 
    session, 
    user, 
    config, 
    materials 
  });

  const [initialReceiptDraft, setInitialReceiptDraft] = useState<Partial<Receipt> | undefined>(undefined);

  const [enterpriseVertical, setEnterpriseVertical] = useState<EnterpriseVerticalId>(() => {
    try {
      const urlVertical = new URLSearchParams(window.location.search).get('vertical');
      if (urlVertical && ['pet_vet', 'medical', 'technical_service', 'general_corporate', 'food_icecream', 'retail_bulk', 'fashion_retail'].includes(urlVertical)) {
        return urlVertical as EnterpriseVerticalId;
      }
    } catch (_) {}

    if (isMasterEmail(user?.email)) {
      const saved = safeStorage.getItem(`rmax_enterprise_vertical_${user?.id || 'default'}`);
      if (saved && ['pet_vet', 'medical', 'technical_service', 'general_corporate', 'food_icecream', 'retail_bulk', 'fashion_retail'].includes(saved)) {
        return saved as EnterpriseVerticalId;
      }
    }
    return detectVerticalFromNiche(user?.niche, user?.profession);
  });

  useEffect(() => {
    try {
      const urlVertical = new URLSearchParams(window.location.search).get('vertical');
      if (urlVertical && ['pet_vet', 'medical', 'technical_service', 'general_corporate', 'food_icecream', 'retail_bulk', 'fashion_retail'].includes(urlVertical)) {
        setEnterpriseVertical(urlVertical as EnterpriseVerticalId);
        return;
      }
    } catch (_) {}

    if (user?.niche || user?.profession) {
      if (isMasterEmail(user?.email)) {
        const saved = safeStorage.getItem(`rmax_enterprise_vertical_${user?.id || 'default'}`);
        if (saved) return;
      }
      setEnterpriseVertical(detectVerticalFromNiche(user?.niche, user?.profession));
    }
  }, [user?.niche, user?.profession, user?.id, user?.email]);

  const handleSelectEnterpriseVertical = (v: EnterpriseVerticalId) => {
    setEnterpriseVertical(v);
    if (isMasterEmail(user?.email)) {
      safeStorage.setItem(`rmax_enterprise_vertical_${user?.id || 'default'}`, v);
    }
  };

  useEffect(() => {
    setViewRef.current = setView;
  }, [setView]);

  const sessionRef = useRef(session);
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  const handleReverseOnboardingComplete = useCallback((niche: string, service: string) => {
    setTempNiche(niche);
    setTempService(service);
    navigateTo('pre-quote');
  }, [navigateTo]);

  const handlePreQuoteComplete = useCallback((newUser: UserProfile, newConfig: FinancialConfig, materialCost: number, timeSpent: number) => {
    setInitialQuoteDraft({
      materialCost,
      timeSpent,
      serviceName: tempService,
      serviceDescription: tempService
    });
    handleOnboardingComplete(newUser, newConfig, 'chat');
  }, [tempService, handleOnboardingComplete, setInitialQuoteDraft]);

  const handleOnboardingWizardComplete = useCallback(async (calculatedHourlyRate: number, fixedCost: number, proLabore: number) => {
    if (!config || !user) return;
    const newConfig = {
      ...config,
      baseHourValue: calculatedHourlyRate,
      monthlyFixedCost: fixedCost,
      proLaboreGoal: proLabore
    };
    setConfig(newConfig);
    if (sessionRef.current?.user) {
      await syncConfigToSupabase(newConfig);
    }
    saveState({ user, financialConfig: newConfig, quotes, materials, recipes, services });
    const isEnterpriseOrSpecialist = Boolean(
      user?.isMasterUser ||
      ['Empresa', 'Empresarial', 'Enterprise', 'empresa', 'enterprise'].includes(user?.plan || '') ||
      user?.plan === 'Especialista' || 
      (user?.plan as string) === 'Premium' || 
      user?.isTrialActive
    );
    const targetHomeView: AppView = isEnterpriseOrSpecialist 
      ? 'erp-hub' 
      : 'home';
    setView(targetHomeView); 
  }, [config, user, quotes, materials, recipes, services, setConfig, syncConfigToSupabase, setView]);

  const handleStartTrial = useCallback(async () => {
    setIsActivatingTrial(true);
    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      if (!currentSession) {
        alert("Você precisa estar conectado para ativar o período de testes.");
        setIsActivatingTrial(false);
        return;
      }

      let activationSuccessful = false;
      try {
        const { data: rpcData, error: rpcError } = await supabase.rpc('activate_user_trial');
        if (!rpcError && rpcData) {
          if (rpcData.success) {
            activationSuccessful = true;
          } else if (rpcData.error) {
            alert(rpcData.error);
            if (currentSession?.user?.id) {
              lastLoadedId.current = null;
              await loadUserData(currentSession.user.id, undefined, true);
            }
            setIsActivatingTrial(false);
            return;
          }
        }
      } catch (rpcErr) {
        console.warn('RPC activate_user_trial falhou ou não existe, tentando Edge Function...', rpcErr);
      }

      if (!activationSuccessful) {
        const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/activate-trial`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${currentSession.access_token}`
          }
        });

        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.error || 'Erro ao ativar período de testes.');
        }
        activationSuccessful = true;
      }

      if (activationSuccessful && currentSession.user.id) {
        lastLoadedId.current = null;
        await loadUserData(currentSession.user.id, undefined, true);
      }
      setUpgradeModalOpen(false);
    } catch (err: any) {
      console.error('Erro ao ativar trial:', err);
      alert(err.message || 'Erro ao ativar o teste gratuito. Verifique sua conexão e tente novamente.');
    } finally {
      setIsActivatingTrial(false);
    }
  }, [loadUserData, lastLoadedId]);

  const simulatedPlanFromUrl = useMemo(() => {
    try {
      const isAllowedToSimulate = Boolean(import.meta.env.DEV || user?.isMasterUser);
      if (!isAllowedToSimulate) return null;

      const p = new URLSearchParams(window.location.search).get('plan')?.toLowerCase();
      if (p === 'free' || p === 'gratis' || p === 'grátis') return 'free';
      if (p === 'pro') return 'pro';
      if (p === 'especialista' || p === 'specialist' || p === 'premium') return 'premium';
      if (p === 'empresa' || p === 'enterprise' || p === 'empresarial') return 'enterprise';
    } catch (_) {}
    return null;
  }, [user?.isMasterUser]);

  const isProUser = Boolean(
    simulatedPlanFromUrl === 'pro' ||
    simulatedPlanFromUrl === 'premium' ||
    simulatedPlanFromUrl === 'enterprise' ||
    (!simulatedPlanFromUrl && (
      user?.isMasterUser ||
      user?.plan === 'Especialista' ||
      user?.plan === 'Empresa' ||
      user?.plan === 'Empresarial' ||
      user?.plan === 'Enterprise' ||
      user?.isTrialActive ||
      (user?.plan === 'Pro' && (user?.subscriptionStatus === 'active' || (user?.accessEndsAt && new Date(user.accessEndsAt) > new Date())))
    ))
  );

  const isSpecialistUser = Boolean(
    simulatedPlanFromUrl === 'premium' ||
    simulatedPlanFromUrl === 'enterprise' ||
    (!simulatedPlanFromUrl && (
      user?.isMasterUser ||
      user?.plan === 'Especialista' ||
      user?.plan === 'Empresa' ||
      user?.plan === 'Empresarial' ||
      user?.plan === 'Enterprise' ||
      user?.isTrialActive
    ))
  );

  const isEnterpriseUser = Boolean(
    simulatedPlanFromUrl === 'enterprise' ||
    (!simulatedPlanFromUrl && (
      user?.isMasterUser ||
      ['Empresa', 'Empresarial', 'Enterprise', 'empresa', 'empresarial', 'enterprise'].includes(user?.plan || '') ||
      ['empresa', 'enterprise'].includes((user as any)?.subscriptionPlanType || '')
    ))
  );

  const canAccessTier = useCallback((requiredTier: 'free' | 'pro' | 'premium' | 'enterprise'): boolean => {
    if (requiredTier === 'free') return true;
    if (requiredTier === 'pro') return isProUser;
    if (requiredTier === 'premium') return isSpecialistUser;
    if (requiredTier === 'enterprise') return isEnterpriseUser;
    return false;
  }, [isProUser, isSpecialistUser, isEnterpriseUser]);

  const handleActionClick = useCallback((
    requiredTier: 'free' | 'pro' | 'premium' | 'enterprise',
    action: () => void,
    featureName: string,
    featureDesc?: string
  ) => {
    if (canAccessTier(requiredTier)) {
      action();
    } else {
      setUpgradeRequiredTier(requiredTier === 'enterprise' ? 'enterprise' : (requiredTier === 'premium' ? 'premium' : 'pro'));
      setPaywallInitialPlan(requiredTier === 'enterprise' ? 'enterprise_anual_pix' : (requiredTier === 'premium' ? 'specialist' : 'annual'));
      setUpgradeFeatureName(featureName);
      setUpgradeFeatureDesc(featureDesc);
      setUpgradeModalOpen(true);
    }
  }, [canAccessTier]);

  const [costCenterInitialTab, setCostCenterInitialTab] = useState<CostCenterTab>('materials');

  const handleOpenChat = useCallback(() => navigateTo('chat'), [navigateTo]);
  const handleOpenDashboard = useCallback(() => navigateTo('dashboard'), [navigateTo]);
  const handleOpenProfile = useCallback((step?: ProfileStep) => navigateTo('profile', step), [navigateTo]);
  const handleOpenMaterials = useCallback(() => {
    setCostCenterInitialTab('materials');
    navigateTo('costs');
  }, [navigateTo]);
  const handleOpenProductCatalog = useCallback(() => {
    setCostCenterInitialTab('products');
    navigateTo('costs');
  }, [navigateTo]);
  const handleOpenErpHub = useCallback(() => navigateTo('erp-hub'), [navigateTo]);
  const handleOpenClients = useCallback(() => navigateTo('clients'), [navigateTo]);
  const handleOpenSuppliers = useCallback(() => navigateTo('suppliers'), [navigateTo]);
  const handleOpenWorkOrders = useCallback(() => navigateTo('work-orders'), [navigateTo]);
  const handleOpenReceipts = useCallback((draft?: Partial<Receipt>) => {
    setInitialReceiptDraft(draft);
    navigateTo('receipts');
  }, [navigateTo]);
  const handleOpenCalendar = useCallback(() => navigateTo('calendar'), [navigateTo]);
  const handleOpenSignatures = useCallback(() => navigateTo('signatures'), [navigateTo]);
  const handleOpenMarketRadar = useCallback(() => navigateTo('market-radar'), [navigateTo]);
  const handleOpenExecutiveCockpit = useCallback(() => navigateTo('executive-cockpit'), [navigateTo]);
  const handleOpenPlatformManagement = useCallback(() => navigateTo('platform-management'), [navigateTo]);
  const handleOpenDirectBooking = useCallback(() => navigateTo('direct-booking'), [navigateTo]);
  const handleOpenPipeline = useCallback(() => navigateTo('pipeline'), [navigateTo]);
  const handleOpenTeamManagement = useCallback(() => {
    handleActionClick(
      'premium',
      () => setTeamModalOpen(true),
      'Gestão de Equipe Multi-Usuário (Até 10 Membros)',
      'No Plano Especialista Premium, você inclui até 10 colaboradores da sua empresa com travas e permissões personalizadas para proteger seus lucros.'
    );
  }, [handleActionClick]);

  const handleOpenEstoqueHub = useCallback(() => navigateTo('estoque_hub'), [navigateTo]);
  const handleOpenProntuarioSoap = useCallback(() => navigateTo('prontuario_soap'), [navigateTo]);
  const handleOpenRetornoVacinas = useCallback(() => navigateTo('retorno_vacinas'), [navigateTo]);
  const handleOpenCofreExames = useCallback(() => navigateTo('cofre_exames'), [navigateTo]);
  const handleOpenPortalContador = useCallback(() => navigateTo('portal_contador'), [navigateTo]);
  const handleOpenComparison = useCallback(() => navigateTo('comparison'), [navigateTo]);
  const handleOpenHistory = useCallback(() => navigateTo('history'), [navigateTo]);
  const handleClearInitialQuoteDraft = useCallback(() => setInitialQuoteDraft(null), [setInitialQuoteDraft]);

  const [specialistQuickActionOpen, setSpecialistQuickActionOpen] = useState(false);

  const shouldShowBottomNav = [
    'home', 'erp-hub', 'chat', 'calendar', 'clients', 'work-orders', 'receipts',
    'costs', 'suppliers', 'signatures', 'market-radar', 'executive-cockpit',
    'pipeline', 'profile', 'dashboard'
  ].includes(view);

  const isSpecialistPage = shouldShowBottomNav;

  const specialistActiveTab: SpecialistTab | null = 
    view === 'erp-hub' || view === 'home' ? 'home' :
    view === 'chat' ? 'orcamento' :
    view === 'calendar' ? 'agenda' :
    view === 'clients' ? 'clientes' :
    view === 'costs' ? 'materiais' :
    view === 'dashboard' ? 'painel' :
    view === 'speed_pos' ? 'pdv' : null;

  const handleSelectSpecialistBottomTab = useCallback((tab: SpecialistTab) => {
    if (tab === 'home') {
      const isEnterpriseOrSpecialist = Boolean(
        user?.isMasterUser ||
        ['Empresa', 'Empresarial', 'Enterprise', 'empresa', 'enterprise'].includes(user?.plan || '') ||
        user?.plan === 'Especialista' || 
        (user?.plan as string) === 'Premium' || 
        user?.isTrialActive
      );
      const targetHome = isEnterpriseOrSpecialist ? 'erp-hub' : 'home';
      if (view === targetHome) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        navigateTo(targetHome);
      }
    } else if (tab === 'orcamento') {
      handleOpenChat();
    } else if (tab === 'agenda') {
      handleOpenCalendar();
    } else if (tab === 'clientes') {
      handleOpenClients();
    } else if (tab === 'materiais') {
      handleOpenMaterials();
    } else if (tab === 'painel') {
      handleOpenDashboard();
    } else if (tab === 'pdv') {
      navigateTo('speed_pos');
    }
  }, [view, user, navigateTo, handleOpenChat, handleOpenCalendar, handleOpenClients, handleOpenMaterials, handleOpenDashboard]);

  // Abertura automática suave do Tour Guiado no primeiro acesso
  useEffect(() => {
    if (!hasLoadedAllData || !user || user.id === 'guest') return;
    const hasSeenLocal = safeStorage.getItem(`rmax_has_seen_tour_${user.id}`) === 'true';
    const hasSeenSession = sessionStorage.getItem(`rmax_has_seen_tour_${user.id}`) === 'true';
    const hasSeenCloud = Boolean(session?.user?.user_metadata?.has_seen_tour);

    if (!hasSeenLocal && !hasSeenSession && !hasSeenCloud) {
      const timer = setTimeout(() => {
        setIsWelcomeTourOpen(true);
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [hasLoadedAllData, user?.id, session?.user?.user_metadata?.has_seen_tour]);

  const handleSpecialistActionSheetNavigate = useCallback((target: ActiveView) => {
    setSpecialistQuickActionOpen(false);
    if (target === 'tour') setIsWelcomeTourOpen(true);
    else if (target === 'orcamento') handleOpenChat();
    else if (target === 'custos') handleOpenMaterials();
    else if (target === 'clientes') handleOpenClients();
    else if (target === 'fornecedores') handleOpenSuppliers();
    else if (target === 'os') handleOpenWorkOrders();
    else if (target === 'recibo') handleOpenReceipts();
    else if (target === 'agenda') handleOpenCalendar();
    else if (target === 'agendar') handleOpenDirectBooking();
    else if (target === 'assinatura') handleOpenSignatures();
    else if (target === 'radar') handleOpenMarketRadar();
    else if (target === 'cockpit') handleOpenExecutiveCockpit();
    else if (target === 'gestao') handleOpenPipeline();
    else if (target === 'cadastro') handleOpenProfile();
    else if (target === 'equipe') handleOpenTeamManagement();
    else if (target === 'estoque_hub') handleOpenEstoqueHub();
    else if (target === 'prontuario_soap') handleOpenProntuarioSoap();
    else if (target === 'retorno_vacinas') handleOpenRetornoVacinas();
    else if (target === 'cofre_exames') handleOpenCofreExames();
    else if (target === 'portal_contador') handleOpenPortalContador();
  }, [
    handleOpenChat, handleOpenMaterials, handleOpenClients, handleOpenSuppliers,
    handleOpenWorkOrders, handleOpenReceipts, handleOpenCalendar, handleOpenDirectBooking,
    handleOpenSignatures, handleOpenMarketRadar, handleOpenExecutiveCockpit, handleOpenPipeline,
    handleOpenProfile, handleOpenTeamManagement, handleOpenEstoqueHub, handleOpenProntuarioSoap,
    handleOpenRetornoVacinas, handleOpenCofreExames, handleOpenPortalContador
  ]);

  // Checkout clínico com baixa atômica de estoque
  const executeConsultationCheckout = useCallback(async (
    consultationId: string,
    consumedItems: ConsumedMaterialItem[],
    paymentMethod: string,
    amountPaid: number
  ) => {
    if (!user?.id || user.id === 'guest' || user.id === 'demo_user') {
      alert('É necessário estar conectado à sua conta para finalizar consultas e registrar baixas de estoque.');
      return { success: false, receiptNumber: '', signatureHash: '' };
    }

    let currentBatches = [...inventoryBatches];
    for (const item of consumedItems) {
      let remainingToDeduct = item.quantity;
      const itemBatches = currentBatches
        .filter(b => b.materialId === item.materialId && b.currentQuantity > 0)
        .sort((a, b) => new Date(a.expirationDate).getTime() - new Date(b.expirationDate).getTime());

      for (const batch of itemBatches) {
        if (remainingToDeduct <= 0) break;
        const deductQty = Math.min(batch.currentQuantity, remainingToDeduct);
        const newQty = batch.currentQuantity - deductQty;

        currentBatches = currentBatches.map(b => b.id === batch.id ? { ...b, currentQuantity: newQty } : b);
        remainingToDeduct -= deductQty;

        const mov: StockMovement = {
          id: `mov_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
          userId: user.id,
          materialId: item.materialId,
          materialName: item.materialName,
          batchId: batch.id,
          batchCode: batch.batchCode,
          type: 'exit_clinical_use',
          quantity: deductQty,
          unitCost: batch.costPrice,
          totalCost: deductQty * batch.costPrice,
          balanceAfter: newQty,
          referenceId: consultationId,
          reasonNotes: 'Baixa automática no checkout da consulta',
          createdAt: new Date().toISOString()
        };
        await handleSaveStockMovement(mov);
      }
    }

    for (const b of currentBatches) {
      await handleSaveInventoryBatch(b);
    }

    const existingConsultation = clinicalConsultations.find(c => c.id === consultationId);
    const finalizedTime = new Date().toISOString();
    const signatureHash = await generateCfmvSignatureHash({
      consultationId,
      patientId: existingConsultation?.patientId || activeConsultationPatient?.id,
      crmv: existingConsultation?.veterinarianCrmv,
      finalizedAt: finalizedTime
    });

    if (existingConsultation) {
      const updatedConsultation: ClinicalConsultation = {
        ...existingConsultation,
        status: 'finalized',
        finalizedAt: finalizedTime,
        professionalSignatureHash: signatureHash
      };
      await handleSaveClinicalConsultation(updatedConsultation);
    }

    let receiptNum = '';
    if (amountPaid > 0) {
      receiptNum = generateReceiptNumber();
      const newReceipt: Receipt = {
        id: `rec_${Date.now()}`,
        userId: user.id,
        receiptNumber: receiptNum,
        totalServiceValue: amountPaid,
        amountPaid: amountPaid,
        remainingBalance: 0,
        paymentMethod: paymentMethod as any,
        receiptType: 'full',
        clientName: activeConsultationPatient?.clientName || 'Cliente / Tutor',
        clientDocument: '',
        description: `Consulta Clínica - Paciente: ${activeConsultationPatient?.name || 'Geral'}`,
        warrantyPeriod: 0,
        warrantyUnit: 'dias',
        issuedAt: finalizedTime
      };
      await handleSaveReceipt(newReceipt);
    }

    setActiveConsultationPatient(null);
    return {
      success: true,
      receiptNumber: receiptNum || 'REC-0000',
      signatureHash
    };
  }, [inventoryBatches, user, clinicalConsultations, activeConsultationPatient, handleSaveStockMovement, handleSaveInventoryBatch, handleSaveClinicalConsultation, handleSaveReceipt]);

  const handleExecuteReceptionCheckout = useCallback(async (checkoutData: {
    consultationId?: string;
    clientId: string;
    clientName: string;
    clientPhone?: string;
    patientId?: string;
    patientName?: string;
    items: ReceptionOrderItem[];
    totalAmount: number;
    paymentMethod: 'pix' | 'cartao_credito' | 'cartao_debito' | 'dinheiro' | 'misto';
    paymentDetails?: any;
    consumedBatches: Array<{
      materialId: string;
      materialName: string;
      batchId?: string;
      batchCode?: string;
      quantity: number;
      unitCost: number;
    }>;
  }) => {
    if (!user?.id || user.id === 'guest' || user.id === 'demo_user') {
      alert('É necessário estar conectado à sua conta para processar checkout na recepção.');
      return { receiptNumber: '' };
    }

    let currentBatches = [...inventoryBatches];
    for (const item of checkoutData.consumedBatches) {
      let remainingToDeduct = item.quantity;
      const itemBatches = currentBatches
        .filter(b => b.materialId === item.materialId && b.currentQuantity > 0)
        .sort((a, b) => new Date(a.expirationDate).getTime() - new Date(b.expirationDate).getTime());

      for (const batch of itemBatches) {
        if (remainingToDeduct <= 0) break;
        const deductQty = Math.min(batch.currentQuantity, remainingToDeduct);
        const newQty = batch.currentQuantity - deductQty;

        currentBatches = currentBatches.map(b => b.id === batch.id ? { ...b, currentQuantity: newQty } : b);
        remainingToDeduct -= deductQty;

        const mov: StockMovement = {
          id: `mov_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
          userId: user.id,
          materialId: item.materialId,
          materialName: item.materialName,
          batchId: batch.id,
          batchCode: batch.batchCode,
          type: 'exit_clinical_use',
          quantity: deductQty,
          unitCost: batch.costPrice,
          totalCost: deductQty * batch.costPrice,
          balanceAfter: newQty,
          referenceId: checkoutData.consultationId,
          reasonNotes: 'Baixa no caixa da recepção (atendimento concluído)',
          createdAt: new Date().toISOString()
        };
        await handleSaveStockMovement(mov);
      }
    }

    for (const b of currentBatches) {
      await handleSaveInventoryBatch(b);
    }

    if (checkoutData.consultationId) {
      const existingConsultation = clinicalConsultations.find(c => c.id === checkoutData.consultationId);
      if (existingConsultation && existingConsultation.status !== 'finalized') {
        const finalizedTime = new Date().toISOString();
        const signatureHash = await generateCfmvSignatureHash({
          consultationId: checkoutData.consultationId,
          patientId: existingConsultation.patientId,
          crmv: existingConsultation.veterinarianCrmv,
          finalizedAt: finalizedTime
        });
        await handleSaveClinicalConsultation({
          ...existingConsultation,
          status: 'finalized',
          finalizedAt: finalizedTime,
          professionalSignatureHash: signatureHash
        });
      }
    }

    const receiptNum = generateReceiptNumber();
    const itemsDescription = checkoutData.items.map(i => `${i.quantity}x ${i.name}`).join(', ');
    const newReceipt: Receipt = {
      id: `rec_${Date.now()}`,
      userId: user.id,
      receiptNumber: receiptNum,
      totalServiceValue: checkoutData.totalAmount,
      amountPaid: checkoutData.totalAmount,
      remainingBalance: 0,
      paymentMethod: checkoutData.paymentMethod as any,
      receiptType: 'full',
      clientName: checkoutData.clientName,
      clientDocument: '',
      description: `Atendimento Recepção: ${itemsDescription.slice(0, 200)}`,
      warrantyPeriod: 0,
      warrantyUnit: 'dias',
      issuedAt: new Date().toISOString()
    };
    await handleSaveReceipt(newReceipt);

    return { receiptNumber: receiptNum };
  }, [inventoryBatches, user, clinicalConsultations, handleSaveStockMovement, handleSaveInventoryBatch, handleSaveClinicalConsultation, handleSaveReceipt]);

  const recordVaccineWhatsAppSent = useCallback((vaccineId: string) => {
    const existing = clinicalVaccinations.find(v => v.id === vaccineId);
    if (existing) {
      handleSaveClinicalVaccination({
        ...existing,
        reminderCount: (existing.reminderCount || 0) + 1,
        lastRemindedAt: new Date().toISOString()
      });
    }
  }, [clinicalVaccinations, handleSaveClinicalVaccination]);

  const { isOffline } = useNetworkStatus();
  const { isActivatingPro } = useMercadoPagoPolling({ session, loadUserData, lastLoadedIdRef: lastLoadedId });

  const handleLogout = async () => {
    try {
      setIsFirstVisit(false);
      localStorage.removeItem('rmax_data');
      setUser(null);
      setConfig(null);
      await signOut();
    } catch (error) {
      console.error('Logout error:', error);
      alert('Erro ao tentar sair da conta.');
    }
  };

  const currentMonthQuotesCount = useMemo(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    return quotes.filter(q => {
      if (!q.createdAt) return true;
      const d = new Date(q.createdAt);
      return !isNaN(d.getTime()) && d.getFullYear() === currentYear && d.getMonth() === currentMonth;
    }).length;
  }, [quotes]);

  const renderWithEnterpriseSuite = useCallback((content: React.ReactNode) => {
    if (!user) return <>{content}</>;
    return (
      <EnterpriseSuiteLayout
        user={user}
        config={config}
        currentView={view}
        vertical={enterpriseVertical}
        onSelectVertical={handleSelectEnterpriseVertical}
        onNavigate={(target) => navigateTo(target)}
        onQuickBudget={handleOpenChat}
        onLogout={handleLogout}
        onOpenHelp={() => handleOpenProfile()}
        onOpenTeamModal={() => setTeamModalOpen(true)}
        onOpenProfile={() => handleOpenProfile()}
        onNewQuote={handleOpenChat}
        onNewWorkOrder={handleOpenWorkOrders}
        onNewBatch={handleOpenEstoqueHub}
        onNewClient={handleOpenClients}
        badges={{
          quotesCount: currentMonthQuotesCount,
          openWorkOrdersCount: workOrders.filter(w => w.status === 'open' || w.status === 'in_progress').length,
          expiringBatchesCount: inventoryBatches.filter(b => {
            if (!b.expirationDate) return false;
            const d = new Date(b.expirationDate);
            return !isNaN(d.getTime()) && d <= new Date(Date.now() + 15 * 24 * 60 * 60 * 1000) && (b.currentQuantity || 0) > 0;
          }).length,
          overdueVaccinesCount: clinicalVaccinations.filter(v => v.nextDueDate && new Date(v.nextDueDate) < new Date() && v.status !== 'completed').length
        }}
      >
        {content}
      </EnterpriseSuiteLayout>
    );
  }, [
    user, config, view, enterpriseVertical, currentMonthQuotesCount, workOrders,
    inventoryBatches, clinicalVaccinations, navigateTo, handleOpenChat, handleLogout,
    handleOpenProfile, handleOpenWorkOrders, handleOpenEstoqueHub, handleOpenClients
  ]);

  const renderView = () => {
    if (view === 'speed_pos') {
      return (
        <EnterpriseViewDispatcher
          view={view}
          user={user}
          config={config}
          isEnterpriseUser={isEnterpriseUser}
          enterpriseVertical={enterpriseVertical}
          quotes={quotes}
          workOrders={workOrders}
          receipts={receipts}
          materials={materials}
          clients={clients}
          suppliers={suppliers}
          appointments={appointments}
          staffMembers={staffMembers}
          storefrontProfile={storefrontProfile}
          inventoryBatches={inventoryBatches}
          stockMovements={stockMovements}
          clinicalPatients={clinicalPatients}
          clinicalConsultations={clinicalConsultations}
          clinicalVaccinations={clinicalVaccinations}
          clinicalExams={clinicalExams}
          accountantAccesses={accountantAccesses}
          currentMonthQuotesCount={currentMonthQuotesCount}
          activeConsultationPatient={activeConsultationPatient}
          setActiveConsultationPatient={setActiveConsultationPatient}
          isAccountantPortalMode={isAccountantPortalMode}
          setIsAccountantPortalMode={setIsAccountantPortalMode}
          onGoBack={handleGoBack}
          onNavigate={navigateTo}
          onOpenChat={handleOpenChat}
          onOpenWorkOrders={handleOpenWorkOrders}
          onOpenEstoqueHub={handleOpenEstoqueHub}
          onOpenProntuarioSoap={handleOpenProntuarioSoap}
          onOpenRetornoVacinas={handleOpenRetornoVacinas}
          onOpenCofreExames={handleOpenCofreExames}
          onOpenPortalContador={handleOpenPortalContador}
          onOpenClients={handleOpenClients}
          onOpenProfile={handleOpenProfile}
          onOpenProductCatalog={handleOpenProductCatalog}
          onOpenTeamModal={() => setTeamModalOpen(true)}
          onSelectEnterpriseVertical={handleSelectEnterpriseVertical}
          onRequestUpgrade={(name, desc) => {
            setUpgradeRequiredTier('enterprise');
            setUpgradeFeatureName(name);
            setUpgradeFeatureDesc(desc);
            setUpgradeModalOpen(true);
          }}
          onLogout={handleLogout}
          onSaveInventoryBatch={handleSaveInventoryBatch}
          onDeleteInventoryBatch={handleDeleteInventoryBatch}
          onSaveStockMovement={handleSaveStockMovement}
          onSaveMaterials={handleSaveMaterials}
          onSaveSupplier={handleSaveSupplier}
          onSaveClinicalPatient={handleSaveClinicalPatient}
          onDeleteClinicalPatient={handleDeleteClinicalPatient}
          onSaveClinicalConsultation={handleSaveClinicalConsultation}
          onSaveClinicalVaccination={handleSaveClinicalVaccination}
          onDeleteClinicalVaccination={handleDeleteClinicalVaccination}
          onSaveClinicalExam={handleSaveClinicalExam}
          onDeleteClinicalExam={handleDeleteClinicalExam}
          onInviteAccountant={handleInviteAccountant}
          onRevokeAccountant={handleRevokeAccountantAccess}
          onExecuteConsultationCheckout={executeConsultationCheckout}
          onExecuteReceptionCheckout={handleExecuteReceptionCheckout}
          onRecordVaccineWhatsAppSent={recordVaccineWhatsAppSent}
        />
      );
    }

    if (session && (!hasLoadedAllData && isLoadingUserData)) {
      return (
        <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6 text-center">
          <div className="w-16 h-16 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin mb-6" />
          <h2 className="text-xl font-bold text-slate-900 mb-2">Abrindo seu ambiente seguro</h2>
          <p className="text-slate-500 text-sm">Preparando tudo para você calcular seus lucros.</p>
        </div>
      );
    }

    // 1. Dispatcher Enterprise (Módulos exclusivos e tela Home/Hub do Plano Empresa)
    const isEnterpriseDedicatedView = [
      'estoque_hub', 'prontuario_soap', 'retorno_vacinas', 'cofre_exames', 'portal_contador', 'recepcao_pdv', 'financial_hub'
    ].includes(view) || (isEnterpriseUser && (view === 'home' || view === 'erp-hub'));

    if (isEnterpriseDedicatedView) {
      return (
        <EnterpriseViewDispatcher
          view={view}
          user={user}
          config={config}
          isEnterpriseUser={isEnterpriseUser}
          enterpriseVertical={enterpriseVertical}
          quotes={quotes}
          workOrders={workOrders}
          receipts={receipts}
          materials={materials}
          clients={clients}
          suppliers={suppliers}
          appointments={appointments}
          staffMembers={staffMembers}
          storefrontProfile={storefrontProfile}
          inventoryBatches={inventoryBatches}
          stockMovements={stockMovements}
          clinicalPatients={clinicalPatients}
          clinicalConsultations={clinicalConsultations}
          clinicalVaccinations={clinicalVaccinations}
          clinicalExams={clinicalExams}
          accountantAccesses={accountantAccesses}
          currentMonthQuotesCount={currentMonthQuotesCount}
          activeConsultationPatient={activeConsultationPatient}
          setActiveConsultationPatient={setActiveConsultationPatient}
          isAccountantPortalMode={isAccountantPortalMode}
          setIsAccountantPortalMode={setIsAccountantPortalMode}
          onGoBack={handleGoBack}
          onNavigate={navigateTo}
          onOpenChat={handleOpenChat}
          onOpenWorkOrders={handleOpenWorkOrders}
          onOpenEstoqueHub={handleOpenEstoqueHub}
          onOpenProntuarioSoap={handleOpenProntuarioSoap}
          onOpenRetornoVacinas={handleOpenRetornoVacinas}
          onOpenCofreExames={handleOpenCofreExames}
          onOpenPortalContador={handleOpenPortalContador}
          onOpenClients={handleOpenClients}
          onOpenProfile={handleOpenProfile}
          onOpenProductCatalog={handleOpenProductCatalog}
          onOpenTeamModal={() => setTeamModalOpen(true)}
          onSelectEnterpriseVertical={handleSelectEnterpriseVertical}
          onRequestUpgrade={(name, desc) => {
            setUpgradeRequiredTier('enterprise');
            setUpgradeFeatureName(name);
            setUpgradeFeatureDesc(desc);
            setUpgradeModalOpen(true);
          }}
          onLogout={handleLogout}
          onSaveInventoryBatch={handleSaveInventoryBatch}
          onDeleteInventoryBatch={handleDeleteInventoryBatch}
          onSaveStockMovement={handleSaveStockMovement}
          onSaveMaterials={handleSaveMaterials}
          onSaveSupplier={handleSaveSupplier}
          onSaveClinicalPatient={handleSaveClinicalPatient}
          onDeleteClinicalPatient={handleDeleteClinicalPatient}
          onSaveClinicalConsultation={handleSaveClinicalConsultation}
          onSaveClinicalVaccination={handleSaveClinicalVaccination}
          onDeleteClinicalVaccination={handleDeleteClinicalVaccination}
          onSaveClinicalExam={handleSaveClinicalExam}
          onDeleteClinicalExam={handleDeleteClinicalExam}
          onInviteAccountant={handleInviteAccountant}
          onRevokeAccountant={handleRevokeAccountantAccess}
          onExecuteConsultationCheckout={executeConsultationCheckout}
          onExecuteReceptionCheckout={handleExecuteReceptionCheckout}
          onRecordVaccineWhatsAppSent={recordVaccineWhatsAppSent}
        />
      );
    }

    // 2. Dispatcher Especialista
    const isSpecialistDedicatedView = [
      'erp-hub', 'clients', 'suppliers', 'work-orders', 'receipts', 'calendar',
      'signatures', 'market-radar', 'executive-cockpit', 'platform-management',
      'direct-booking', 'pipeline'
    ].includes(view);

    if (isSpecialistDedicatedView) {
      return (
        <SpecialistViewDispatcher
          view={view}
          user={user}
          config={config}
          clients={clients}
          suppliers={suppliers}
          workOrders={workOrders}
          receipts={receipts}
          appointments={appointments}
          staffMembers={staffMembers}
          subscriptionClubs={subscriptionClubs}
          storefrontProfile={storefrontProfile}
          quotes={quotes}
          services={services}
          initialReceiptDraft={initialReceiptDraft}
          setInitialReceiptDraft={setInitialReceiptDraft}
          currentMonthQuotesCount={currentMonthQuotesCount}
          isEnterpriseUser={isEnterpriseUser}
          enterpriseVertical={enterpriseVertical}
          renderWrapper={renderWithEnterpriseSuite}
          onGoBack={handleGoBack}
          onNavigate={navigateTo}
          onOpenChat={handleOpenChat}
          onOpenMaterials={handleOpenMaterials}
          onOpenClients={handleOpenClients}
          onOpenSuppliers={handleOpenSuppliers}
          onOpenWorkOrders={handleOpenWorkOrders}
          onOpenReceipts={handleOpenReceipts}
          onOpenCalendar={handleOpenCalendar}
          onOpenDirectBooking={handleOpenDirectBooking}
          onOpenSignatures={handleOpenSignatures}
          onOpenMarketRadar={handleOpenMarketRadar}
          onOpenExecutiveCockpit={handleOpenExecutiveCockpit}
          onOpenPipeline={handleOpenPipeline}
          onOpenProfile={handleOpenProfile}
          onOpenTeamManagement={handleOpenTeamManagement}
          onOpenEstoqueHub={handleOpenEstoqueHub}
          onOpenProntuarioSoap={handleOpenProntuarioSoap}
          onOpenRetornoVacinas={handleOpenRetornoVacinas}
          onOpenCofreExames={handleOpenCofreExames}
          onOpenPortalContador={handleOpenPortalContador}
          onOpenHistory={handleOpenHistory}
          onSaveClient={handleSaveClient}
          onDeleteClient={handleDeleteClient}
          onSaveSupplier={handleSaveSupplier}
          onDeleteSupplier={handleDeleteSupplier}
          onSaveWorkOrder={handleSaveWorkOrder}
          onDeleteWorkOrder={handleDeleteWorkOrder}
          onSaveReceipt={handleSaveReceipt}
          onDeleteReceipt={handleDeleteReceipt}
          onSaveAppointment={handleSaveAppointment}
          onDeleteAppointment={handleDeleteAppointment}
          onUpdateAppointmentStatus={handleUpdateAppointmentStatus}
          onRefreshAppointments={refreshAppointments}
          onSaveStaffMember={handleSaveStaffMember}
          onDeleteStaffMember={handleDeleteStaffMember}
          onSaveSubscriptionClub={handleSaveSubscriptionClub}
          onDeleteSubscriptionClub={handleDeleteSubscriptionClub}
          onSaveSignature={handleSaveSignature}
          onSaveQuote={handleSaveQuote}
          onSaveStorefrontProfile={handleSaveStorefrontProfile}
          onUpdateConfigRate={async (newRate) => {
            if (!config) return;
            const newConfig = { ...config, baseHourValue: newRate };
            setConfig(newConfig);
            if (sessionRef.current?.user) {
              await syncConfigToSupabase(newConfig);
            }
          }}
          onLogout={handleLogout}
          onOpenPaywall={(plan) => {
            setPaywallInitialPlan(plan || (user?.plan === 'Pro' ? 'specialist' : 'annual'));
            setPaywallModalOpen(true);
          }}
          canAccessTier={canAccessTier}
          handleActionClick={handleActionClick}
          setAppointments={setAppointments}
        />
      );
    }

    // 3. Dispatcher Core & Grátis/Pro
    const coreRender = (
      <CoreViewDispatcher
        view={view}
        user={user}
        config={config}
        quotes={quotes}
        materials={materials}
        recipes={recipes}
        services={services}
        clients={clients}
        isFirstVisit={isFirstVisit}
        profileStep={profileStep}
        costCenterInitialTab={costCenterInitialTab}
        initialQuoteDraft={initialQuoteDraft}
        tempNiche={tempNiche}
        tempService={tempService}
        currentMonthQuotesCount={currentMonthQuotesCount}
        isActivatingTrial={isActivatingTrial}
        isEnterpriseUser={isEnterpriseUser}
        renderWrapper={renderWithEnterpriseSuite}
        onGoBack={handleGoBack}
        onOpenChat={handleOpenChat}
        onOpenDashboard={handleOpenDashboard}
        onOpenProfile={handleOpenProfile}
        onOpenMaterials={handleOpenMaterials}
        onOpenErpHub={handleOpenErpHub}
        onOpenPlatformManagement={handleOpenPlatformManagement}
        onOpenReformaLote={() => setViewStable('reforma_lote')}
        onOpenTour={() => setIsWelcomeTourOpen(true)}
        onOpenComparison={handleOpenComparison}
        onOpenHistory={handleOpenHistory}
        onOpenEstoqueHub={handleOpenEstoqueHub}
        onStartTrial={handleStartTrial}
        onOpenPaywall={(plan) => {
          setPaywallInitialPlan(plan || (user?.plan === 'Pro' ? 'specialist' : 'annual'));
          setPaywallModalOpen(true);
        }}
        onLogout={handleLogout}
        onSaveQuote={handleSaveQuote}
        onUpdateQuoteStatus={handleUpdateQuoteStatus}
        onReuseQuote={handleReuseQuote}
        onSaveMaterials={handleSaveMaterials}
        onSaveRecipe={handleSaveRecipe}
        onDeleteRecipe={handleDeleteRecipe}
          onSaveService={handleSaveService}
          onDeleteService={handleDeleteService}
        onProfileUpdate={handleProfileUpdate}
        onResetNicheData={handleResetNicheData}
        onReverseOnboardingComplete={handleReverseOnboardingComplete}
        onPreQuoteComplete={handlePreQuoteComplete}
        onOnboardingComplete={handleOnboardingComplete}
        onOnboardingWizardComplete={handleOnboardingWizardComplete}
        onClearInitialQuoteDraft={handleClearInitialQuoteDraft}
      />
    );

    if (coreRender) return coreRender;

    // 4. Fallback de Onboarding
    if (session && hasLoadedAllData) {
      return (
        <Onboarding
          onComplete={handleOnboardingComplete}
          initialUser={user}
          initialConfig={config}
        />
      );
    }

    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6 text-center">
        <div className="w-16 h-16 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin mb-6" />
        <h2 className="text-xl font-bold text-slate-900 mb-2">Finalizando carregamento</h2>
      </div>
    );
  };

  return (
    <>
      <div 
        className={`w-full min-h-dvh flex flex-col ${
          shouldShowBottomNav && !isKeyboardOpen 
            ? 'pb-[calc(4.5rem+env(safe-area-inset-bottom,16px))] lg:pb-0' 
            : ''
        }`}
      >
        {renderView()}
      </div>

      <GlobalModalsContainer
        view={view}
        user={user}
        isOffline={isOffline}
        isActivatingPro={isActivatingPro}
        isActivatingTrial={isActivatingTrial}
        upgradeModalOpen={upgradeModalOpen}
        setUpgradeModalOpen={setUpgradeModalOpen}
        upgradeRequiredTier={upgradeRequiredTier}
        upgradeFeatureName={upgradeFeatureName}
        upgradeFeatureDesc={upgradeFeatureDesc}
        paywallModalOpen={paywallModalOpen}
        setPaywallModalOpen={setPaywallModalOpen}
        paywallInitialPlan={paywallInitialPlan}
        setPaywallInitialPlan={setPaywallInitialPlan}
        teamModalOpen={teamModalOpen}
        setTeamModalOpen={setTeamModalOpen}
        onStartTrial={handleStartTrial}
        isSpecialistPage={isSpecialistPage}
        shouldShowBottomNav={shouldShowBottomNav}
        isKeyboardOpen={isKeyboardOpen}
        isEnterpriseUser={isEnterpriseUser}
        isSpecialistUser={isSpecialistUser}
        specialistActiveTab={specialistActiveTab}
        onSelectSpecialistBottomTab={handleSelectSpecialistBottomTab}
        specialistQuickActionOpen={specialistQuickActionOpen}
        setSpecialistQuickActionOpen={setSpecialistQuickActionOpen}
        onSpecialistActionSheetNavigate={handleSpecialistActionSheetNavigate}
      />

      <WelcomeGuidedTourModal
        isOpen={isWelcomeTourOpen}
        onClose={() => setIsWelcomeTourOpen(false)}
        onStartFirstQuote={() => {
          setIsWelcomeTourOpen(false);
          handleOpenChat();
        }}
        userName={user?.ownerName || user?.fullName}
        userId={user?.id}
      />
    </>
  );
}
